/**
 * History API module for Admin History Browser
 * Provides functions to fetch and manage history entries
 */

import { api } from "aldoo-ra/API"

// Use history endpoint
const historyAPI = api("history")

export const HistoryAPI = {

  /**
   * List history entries with pagination and filters
   * @param {Object} options - Filter options
   * @returns {Promise<{result: boolean, data: Array, totalItems: number}>}
   */
  ListHistory: async (options = {}) => {
    try {
      const {
        page = 1,
        itemsPerPage = 25,
        targetModel = null,
        target = null,
        user = null,
        tags = [],
        startDate = null,
        endDate = null,
        search = null,
        action = null
      } = options

      const response = await historyAPI({
        list: {
          page,
          itemsPerPage,
          targetModel,
          target,
          user,
          tags,
          startDate,
          endDate,
          search,
          action
        }
      })

      return {
        result: response.result || true,
        data: response.data || [],
        totalItems: response.totalItems || 0,
        error: response.error || null
      }
    } catch (error) {
      console.error("Failed to list history:", error)
      return {
        result: false,
        data: [],
        totalItems: 0,
        error: error.message
      }
    }
  },

  /**
   * Get history statistics
   * @returns {Promise<{result: boolean, statistics: Object}>}
   */
  GetStatistics: async () => {
    try {
      const response = await historyAPI({
        statistics: {}
      })

      return {
        result: response.result || true,
        statistics: response.statistics || {},
        error: response.error || null
      }
    } catch (error) {
      console.error("Failed to get statistics:", error)
      return {
        result: false,
        statistics: {},
        error: error.message
      }
    }
  },

  /**
   * Get history for a specific target
   * @param {string} targetId - Target object ID
   * @param {string} targetModel - Target model name
   * @returns {Promise<{result: boolean, data: Array}>}
   */
  GetTargetHistory: async (targetId, targetModel) => {
    try {
      const response = await historyAPI({
        'get-target': {
          target: targetId,
          targetModel
        }
      })

      return {
        result: response.result || true,
        data: response.data || [],
        error: response.error || null
      }
    } catch (error) {
      console.error("Failed to get target history:", error)
      return {
        result: false,
        data: [],
        error: error.message
      }
    }
  },

  /**
   * Clear history entries
   * @param {Object} options - Clear options
   * @returns {Promise<{result: boolean}>}
   */
  ClearHistory: async (options = {}) => {
    try {
      const response = await historyAPI({
        clear: options
      })

      return {
        result: response.result || true,
        deletedCount: response.deletedCount || 0,
        error: response.error || null
      }
    } catch (error) {
      console.error("Failed to clear history:", error)
      return {
        result: false,
        deletedCount: 0,
        error: error.message
      }
    }
  },

  /**
   * Get all available tags from history entries
   * @returns {Promise<{result: boolean, tags: Array<string>}>}
   */
  GetTags: async () => {
    try {
      const response = await historyAPI({
        'get-tags': {}
      })

      return {
        result: response.result || true,
        tags: response.tags || [],
        error: response.error || null
      }
    } catch (error) {
      console.error("Failed to get tags:", error)
      return {
        result: false,
        tags: [],
        error: error.message
      }
    }
  },

  /**
   * Get all users who have history entries
   * @returns {Promise<{result: boolean, users: Array<{userId: string, author: string}>}>}
   */
  GetUsers: async () => {
    try {
      const response = await historyAPI({
        'get-users': {}
      })

      return {
        result: response.result || true,
        users: response.users || [],
        error: response.error || null
      }
    } catch (error) {
      console.error("Failed to get users:", error)
      return {
        result: false,
        users: [],
        error: error.message
      }
    }
  },

  /**
   * Get history configuration
   * @returns {Promise<{result: boolean, config: Object, paramDefinitions: Object}>}
   */
  GetConfig: async () => {
    try {
      const response = await historyAPI({
        'get-config': {}
      })

      return {
        result: response.result || true,
        config: response.config || {},
        paramDefinitions: response.paramDefinitions || {},
        error: response.error || null
      }
    } catch (error) {
      console.error("Failed to get config:", error)
      return {
        result: false,
        config: {},
        paramDefinitions: {},
        error: error.message
      }
    }
  }
}

export default HistoryAPI
